import React, { useState } from "react";
import InputField from "components/common/controls/InputField";
import FormGroup from "components/common/FormGroup";
import CurrentUserResponse from "interfaces/response/CurrentUserResponse";
import UpdateCurrentUserRequest from "interfaces/request/UpdateCurrentUserRequest";
import {
  updateUserDetailInfo,
  setCurrentUser,
  deleteUser,
} from "store/slices/UserSlice";
import { userLogout } from "store/slices/AuthenticationSlice";
import { AppDispatch, RootState } from "store/store";
import { unwrapResult } from "@reduxjs/toolkit";
import EditorActionFooter from "components/common/EditorActionFooter";
import LinkButton from "components/common/LinkButton";
import DeleteAccountConfirmationPopup from "./DeleteAccountConfirmationPopup";
import { connect } from "react-redux";
import UserIdentifierWithVerifcation from "./UserIdentifierWithVerifcation";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";
import Loader from "components/common/controls/Loader";
import FormGroupWrapper from "./UserAccountFormFormGroupWrapper";

interface Props {
  user?: CurrentUserResponse | null;
  dispatch: AppDispatch;
  onSuccess?: () => void;
  onCancel?: () => void;
  isLoading: boolean;
}

interface FormState extends Omit<CurrentUserResponse, "products" | "fullName"> {
  phoneCode?: string;
  phoneNumber?: string;
}

export function UserAccountEditForm(props: Props) {
  const { user, onSuccess, onCancel, dispatch } = props;

  const [userDetail, setUserDetail] = useState<FormState>(
    user ?? {
      id: 0,
      firstName: "",
      lastName: "",
      companyName: "",
      email: "",
      isActive: true,
      phone: "",
      isEmailVerified: false,
      isPhoneVerified: false,
    }
  );

  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleInputValueChange = ({ event }: any) => {
    const targetName = event.target.name;
    const value = event.target.value;
    setUserDetail((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  const handleSubmit = (evt: React.SyntheticEvent) => {
    evt.preventDefault();
    const userRequest: UpdateCurrentUserRequest = {
      firstName: userDetail.firstName,
      lastName: userDetail.lastName,
      companyName: userDetail.companyName,
    };

    if (userDetail && userDetail.id) {
      dispatch(
        updateUserDetailInfo({
          userId: userDetail.id,
          userRequest: userRequest,
        })
      )
        .then(unwrapResult)
        .then((response: CurrentUserResponse) => {
          dispatch(setCurrentUser(response));
          onSuccess?.();
        });
    }
  };

  const ActionFooter = () => {
    return (
      <div>
        <div className="float-left py-2.5">
          <LinkButton
            className="text-primaryRed"
            onClick={handleDeleteAccountClick}
          >
            Delete Account
          </LinkButton>
        </div>
        <div className="float-right">
          <EditorActionFooter
            onCancel={onCancel}
            useSubmitBehaviorOnApply={true}
            applyButtonCaption="SAVE CHANGES"
          />
        </div>
      </div>
    );
  };

  const handleDeleteAccountClick = () => {
    setIsDeletingAccount(true);
  };

  const handleConfirmDeleteAccount = () => {
    if (user?.id) {
      dispatch(deleteUser(user.id))
        .then(unwrapResult)
        .then(() => {
          dispatch(userLogout());
        })
        .finally(() => {
          setIsDeletingAccount(false);
        });
    }
  };

  const handleCancelDeleteAccount = () => {
    setIsDeletingAccount(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <FormGroupWrapper>
          <FormGroup label="First name">
            <InputField
              placeholder="First name"
              name="firstName"
              defaultValue={user?.firstName}
              maxLength={50}
              required
              id="firstName"
              label="First name"
              onChange={handleInputValueChange}
            />
          </FormGroup>
        </FormGroupWrapper>
        <FormGroupWrapper>
          <FormGroup label="Last name">
            <InputField
              placeholder="Last name"
              name="lastName"
              defaultValue={user?.lastName}
              maxLength={50}
              required
              id="lastName"
              label="Last name"
              onChange={handleInputValueChange}
            />
          </FormGroup>
        </FormGroupWrapper>
        <FormGroupWrapper marginClass="mb-7">
          <FormGroup label="Email">
            <UserIdentifierWithVerifcation
              verificationType={SignInSignUpOption.Email}
              emailInputProps={{
                defaultValue: user?.email,
                required: true,
              }}
              isEmailVerified={user?.isEmailVerified}
            />
          </FormGroup>
        </FormGroupWrapper>
        <FormGroupWrapper marginClass="mb-7">
          <FormGroup label="Phone (inc. country code)">
            <UserIdentifierWithVerifcation
              verificationType={SignInSignUpOption.Phone}
              phoneInputProps={{
                value: user?.phone || "",
                required: true,
              }}
              isPhoneVerified={user?.isPhoneVerified}
            />
          </FormGroup>
        </FormGroupWrapper>
        <FormGroupWrapper>
          <FormGroup label="Company">
            <InputField
              name="companyName"
              maxLength={255}
              id="companyName"
              label="Company"
              defaultValue={user?.companyName}
              onChange={handleInputValueChange}
            />
          </FormGroup>
        </FormGroupWrapper>
        <div className="mt-6">
          <ActionFooter />
        </div>
      </form>

      {isDeletingAccount && (
        <DeleteAccountConfirmationPopup
          onApply={handleConfirmDeleteAccount}
          onCancel={handleCancelDeleteAccount}
        />
      )}

      <Loader isVisible={props.isLoading} />
    </>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    isLoading: state.userData.loading,
  };
};

export default connect(mapStateToProps)(UserAccountEditForm);
